/**
 * Which locations the emissions map cannot place well, and could place better if someone
 * filled in a missing detail.
 *
 * Note `countryId` is NOT NULL on the location table and every seeded country has a centroid,
 * so a location is essentially never unplaceable outright. The real gap is precision: a site
 * with no usable postcode gets pinned to the middle of its country. That is the thing worth
 * asking a user to fix, so it is what "needs attention" means here.
 */

/**
 * Countries whose postcodes the backend can resolve to a district centroid (the UK plus the
 * Crown Dependencies, which share its postcode system). Mirrors `UK_POSTCODE_COUNTRY_IDS` in
 * `gaia-carbon-be/backend/lib/geo/geocode.js` — the backend is the source of truth.
 *
 * Outside these, a postcode buys no extra precision (there is no international postcode data),
 * so a country-level pin is already the best available and must NOT be flagged as incomplete.
 */
export const UK_POSTCODE_COUNTRY_IDS = ['GBR', 'GGY', 'JEY', 'IMN'];

export interface IGeocodableLocation {
  postcode?: string | null;
  countryId?: string | null;
  latitude?: number | null;
  geocodePrecision?: string | null;
}

/**
 * True when the map cannot place this location precisely AND a user could fix that.
 *
 * - No coordinates at all — nothing has been resolved (e.g. predates the backfill).
 * - Pinned at country level in a country where a postcode WOULD resolve to a district.
 *
 * A non-UK site sitting at its country centroid is not flagged: that is already as precise as
 * the data allows, and nagging about it would ask for something that changes nothing.
 */
export function needsGeocodeAttention(location: IGeocodableLocation): boolean {
  if (location.latitude === null || location.latitude === undefined) return true;
  if (location.geocodePrecision !== 'country') return false;
  const countryId = String(location.countryId ?? '').toUpperCase();
  return UK_POSTCODE_COUNTRY_IDS.includes(countryId);
}

/** Short explanation of why a location is flagged, for display next to it. */
export function geocodeAttentionReason(location: IGeocodableLocation): string {
  if (location.latitude === null || location.latitude === undefined) {
    return 'Not placed on the map yet';
  }
  return location.postcode
    ? 'Postcode not recognised, so shown at the country centre'
    : 'No postcode, so shown at the country centre';
}
